import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateGridActionIconSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderFlushSx,
  harmonateSectionHeaderSx,
} from '../../../theme/harmonateStyles';

import {
  ACTION_OPTIONS,
  APPROVAL_ACTIONS,
  VIEW_ACTIONS,
  WORKFLOW_TYPE_OPTIONS,
  CONDITION_OPS,
  mapCatalogVariables,
  parseConditionBuilder,
  buildConditionJson,
  emptyConditionClause,
} from './workflowVariables';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const getSessionTenantId = () => localStorage.getItem('tenant_id') || '';
const isHarmonateUser = () => isServerHarmonateUser();
const getUserName = () =>
  localStorage.getItem('user_name')
  || localStorage.getItem('username')
  || localStorage.getItem('email')
  || 'admin';
const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const parseActions = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((a) => String(a).trim().toUpperCase()).filter((a) => ACTION_OPTIONS.includes(a));
  }
  if (!raw) return [...APPROVAL_ACTIONS];
  return String(raw)
    .split(/[,|;]/)
    .map((a) => a.trim().toUpperCase())
    .filter((a) => ACTION_OPTIONS.includes(a));
};

const emptyStep = (sequence = 1) => ({
  stepSequence: sequence,
  stepCode: '',
  stepName: '',
  stepType: 'APPROVAL',
  assignedRoleId: '',
  assignedRoleName: '',
  allowedActions: [...APPROVAL_ACTIONS],
  conditionExpression: '',
  conditionJoin: 'AND',
  conditionClauses: [emptyConditionClause()],
  nextStepOnTrue: '',
  nextStepOnFalse: '',
  nextStepOnApprove: '',
  nextStepOnReject: '',
  formulaExpression: '',
  status: 'ACTIVE',
});

const emptyForm = (tenantId = '') => ({
  tenantId,
  propertyId: '',
  propertyName: '',
  workflowCode: '',
  workflowName: '',
  workflowType: 'AP_INVOICE',
  description: '',
  status: 'ACTIVE',
  steps: [emptyStep(1)],
});

const ApWorkflowSetup = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFLOW);
  const sessionTenantId = getSessionTenantId();
  const harmonateOnly = isHarmonateUser();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(sessionTenantId);
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [tenantRoles, setTenantRoles] = useState([]);
  const [tenantProperties, setTenantProperties] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(sessionTenantId));
  const [saving, setSaving] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const [stepsDrawerWorkflow, setStepsDrawerWorkflow] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [catalogVariables, setCatalogVariables] = useState([]);
  const [catalogScopeLabel, setCatalogScopeLabel] = useState('');
  const [catalogFromDb, setCatalogFromDb] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');

  const formTenantId = form.tenantId || selectedTenantId;
  const formTenant = useMemo(
    () => tenants.find((t) => String(t.id) === String(formTenantId)),
    [tenants, formTenantId],
  );

  const loadTenants = useCallback(async () => {
    try {
      const data = await apiFetch('conductor/tenants');
      const list = (Array.isArray(data) ? data : [])
        .map((t) => ({
          id: String(t.id || t.tenantId || ''),
          name: t.name || t.tenantName || String(t.id || ''),
        }))
        .filter((t) => t.id);
      setTenants(list);
      setSelectedTenantId((prev) => {
        if (prev && list.some((t) => t.id === String(prev))) return String(prev);
        if (sessionTenantId && list.some((t) => t.id === String(sessionTenantId))) {
          return String(sessionTenantId);
        }
        return list[0]?.id || '';
      });
    } catch (err) {
      setTenants([]);
      showError(err.message || 'Failed to load tenants');
    }
  }, [sessionTenantId, showError]);

  const loadTenantProperties = useCallback(async (tenantId) => {
    if (!tenantId) {
      setTenantProperties([]);
      return;
    }
    try {
      const data = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
      const mapped = (Array.isArray(data) ? data : [])
        .map((item) => ({
          id: String(item.propertyId || item.id || ''),
          name: item.propertyName
            || item.businessUnitName
            || item.name
            || item.propertyCode
            || String(item.propertyId || ''),
        }))
        .filter((p) => p.id);
      setTenantProperties(mapped);
    } catch {
      setTenantProperties([]);
    }
  }, []);

  const loadTenantRoles = useCallback(async (tenantId) => {
    if (!tenantId) {
      setTenantRoles([]);
      return;
    }
    try {
      const [tenantRolesData, masterRoles] = await Promise.all([
        apiFetch(`conductor/roles/tenant/tenant/${tenantId}`),
        apiFetch('conductor/roles'),
      ]);
      const masterById = new Map(
        (Array.isArray(masterRoles) ? masterRoles : []).map((r) => [r.id, r]),
      );
      const mapped = (Array.isArray(tenantRolesData) ? tenantRolesData : []).map((tr) => {
        const master = masterById.get(tr.roleId);
        return {
          roleId: tr.roleId,
          roleName: tr.roleName || tr.role_name || master?.roleName || master?.role_name || 'Unknown',
        };
      });
      setTenantRoles(mapped);
    } catch {
      setTenantRoles([]);
    }
  }, []);

  const loadWorkflows = useCallback(async (tenantId) => {
    if (!tenantId) {
      setWorkflows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await apiFetch(`conductor/approval-workflows/tenant/${tenantId}`);
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (err) {
      showError(err.message || 'Failed to load approval workflows');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (!harmonateOnly) return;
    loadTenants();
  }, [harmonateOnly, loadTenants]);

  useEffect(() => {
    if (!harmonateOnly || !selectedTenantId) {
      setWorkflows([]);
      return;
    }
    setSelectionModel([]);
    loadWorkflows(selectedTenantId);
  }, [harmonateOnly, selectedTenantId, loadWorkflows]);

  useEffect(() => {
    if (!harmonateOnly) return;
    const roleTenantId = dialogOpen ? formTenantId : selectedTenantId;
    loadTenantRoles(roleTenantId);
    loadTenantProperties(roleTenantId);
  }, [harmonateOnly, dialogOpen, formTenantId, selectedTenantId, loadTenantRoles, loadTenantProperties]);

  const loadVariableCatalog = useCallback(async (workflowType) => {
    const type = workflowType || 'AP_INVOICE';
    setCatalogLoading(true);
    setCatalogError('');
    try {
      // Single call: backend merges COMMON + AP_INVOICE (or other type) from catalog table
      const data = await apiFetch(
        `conductor/approval-workflows/variables?workflow_type=${encodeURIComponent(type)}`,
      );
      setCatalogVariables(mapCatalogVariables(data?.variables));
      setCatalogScopeLabel(data?.scopeLabel || '');
      setCatalogFromDb(Boolean(data?.fromCatalog));
      if (!data?.fromCatalog || !Array.isArray(data?.variables) || data.variables.length === 0) {
        setCatalogError('No variables in catalog for this workflow type. Seed approval_workflow_variable_catalog.');
      }
    } catch (err) {
      setCatalogVariables([]);
      setCatalogScopeLabel('');
      setCatalogFromDb(false);
      setCatalogError(err.message || 'Failed to load variable catalog');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    loadVariableCatalog(form.workflowType);
    return undefined;
  }, [dialogOpen, form.workflowType, loadVariableCatalog]);

  const openCreate = () => {
    if (!canAdd) return;
    if (!selectedTenantId) {
      showError('Select a tenant first');
      return;
    }
    setEditingId(null);
    setForm(emptyForm(selectedTenantId));
    setDialogOpen(true);
  };

  const openEdit = (wf) => {
    if (!canEdit || !wf) return;
    const wfTenantId = wf.tenantId ? String(wf.tenantId) : selectedTenantId;
    setEditingId(wf.approvalWorkflowMasterId);
    if (wfTenantId && wfTenantId !== selectedTenantId) {
      setSelectedTenantId(wfTenantId);
    }
    setForm({
      tenantId: wfTenantId,
      propertyId: wf.propertyId ? String(wf.propertyId) : '',
      propertyName: wf.propertyName || '',
      workflowCode: wf.workflowCode || '',
      workflowName: wf.workflowName || '',
      workflowType: wf.workflowType || '',
      description: wf.description || '',
      status: wf.status || 'ACTIVE',
      steps: (wf.steps || []).length
        ? (wf.steps || []).map((s, idx) => ({
            approvalWorkflowStepId: s.approvalWorkflowStepId || null,
            stepSequence: s.stepSequence ?? idx + 1,
            stepCode: s.stepCode || '',
            stepName: s.stepName || '',
            stepType: s.stepType || 'APPROVAL',
            assignedRoleId: s.assignedRoleId || '',
            assignedRoleName: s.assignedRoleName || '',
            allowedActions: parseActions(s.allowedActions),
            conditionExpression: s.conditionExpression || '',
            conditionJoin: parseConditionBuilder(s.conditionExpression).join,
            conditionClauses: parseConditionBuilder(s.conditionExpression).clauses,
            nextStepOnTrue: s.nextStepOnTrue ?? '',
            nextStepOnFalse: s.nextStepOnFalse ?? '',
            nextStepOnApprove: s.nextStepOnApprove ?? '',
            nextStepOnReject: s.nextStepOnReject ?? '',
            formulaExpression: s.formulaExpression || '',
            status: s.status || 'ACTIVE',
          }))
        : [emptyStep(1)],
    });
    setDialogOpen(true);
  };

  const handleFormTenantChange = (tenantId) => {
    setForm((prev) => ({
      ...prev,
      tenantId,
      propertyId: '',
      propertyName: '',
      steps: (prev.steps || []).map((s) => ({
        ...s,
        assignedRoleId: '',
        assignedRoleName: '',
      })),
    }));
  };

  const updateStep = (index, patch) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      steps[index] = { ...steps[index], ...patch };
      return { ...prev, steps };
    });
  };

  const availableVariables = catalogVariables;

  const workflowTypeLabel = useMemo(() => {
    if (catalogScopeLabel) return catalogScopeLabel;
    const opt = WORKFLOW_TYPE_OPTIONS.find((o) => o.value === (form.workflowType || 'AP_INVOICE'));
    return opt?.label || form.workflowType || 'Payment Intelligence';
  }, [catalogScopeLabel, form.workflowType]);

  const applyConditionBuilder = (index, { join, clauseIndex, patch, add, remove }) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      const current = steps[index] || {};
      const parsed = parseConditionBuilder(current.conditionExpression);
      let nextJoin = join !== undefined ? join : (current.conditionJoin || parsed.join || 'AND');
      let clauses = (current.conditionClauses || parsed.clauses).map((clause) => ({ ...clause }));
      if (!clauses.length) clauses = [emptyConditionClause()];
      if (add) {
        clauses = [...clauses, emptyConditionClause()];
        nextJoin = nextJoin || 'AND';
      } else if (remove != null && clauses.length > 1) {
        clauses = clauses.filter((_, i) => i !== remove);
      } else if (clauseIndex != null && patch) {
        clauses[clauseIndex] = { ...clauses[clauseIndex], ...patch };
      }
      if (clauses.length <= 1) nextJoin = 'AND';
      steps[index] = {
        ...current,
        conditionJoin: nextJoin,
        conditionClauses: clauses,
        conditionExpression: buildConditionJson({ join: nextJoin, clauses }),
      };
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setForm((prev) => ({
      ...prev,
      steps: [...prev.steps, emptyStep(prev.steps.length + 1)],
    }));
  };

  const removeStep = (index) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, stepSequence: i + 1 })),
    }));
  };

  const save = async () => {
    if ((editingId && !canEdit) || (!editingId && !canAdd)) return;
    const saveTenantId = form.tenantId || selectedTenantId;
    if (!saveTenantId) {
      showError('Select a tenant for this workflow');
      return;
    }
    if (!form.workflowName.trim() || !form.workflowCode.trim()) {
      showError('Workflow name and code are required');
      return;
    }
    if (!form.workflowType.trim()) {
      showError('Workflow type is required');
      return;
    }
    const selectedType = WORKFLOW_TYPE_OPTIONS.find((o) => o.value === form.workflowType.trim().toUpperCase());
    if (!selectedType?.enabled) {
      showError('Select an enabled workflow type (currently only Payment Intelligence / AP_INVOICE)');
      return;
    }
    if (!form.steps.length || form.steps.some((s) => !s.stepName.trim())) {
      showError('Each step needs a name');
      return;
    }
    if (form.steps.some((s) => !parseActions(s.allowedActions).length)) {
      showError('Each step needs at least one allowed action');
      return;
    }
    if ((form.status || 'ACTIVE').toUpperCase() === 'ACTIVE') {
      const duplicate = (workflows || []).find((wf) => {
        if (editingId && String(wf.approvalWorkflowMasterId) === String(editingId)) return false;
        if (String(wf.tenantId) !== String(saveTenantId)) return false;
        if (String(wf.workflowType || '').toUpperCase() !== form.workflowType.trim().toUpperCase()) return false;
        if (String(wf.status || '').toUpperCase() !== 'ACTIVE') return false;
        const wfPropertyId = wf.propertyId ? String(wf.propertyId) : '';
        const formPropertyId = form.propertyId ? String(form.propertyId) : '';
        return wfPropertyId === formPropertyId;
      });
      if (duplicate) {
        const scope = form.propertyId
          ? `business unit "${duplicate.propertyName || form.propertyName || 'selected'}"`
          : 'tenant default (no business unit)';
        showError(
          `An active workflow already exists for this ${scope}: "${duplicate.workflowName}". Set that one to INACTIVE, or pick a different business unit.`,
        );
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        tenantId: saveTenantId,
        propertyId: form.propertyId || null,
        propertyName: form.propertyId
          ? (tenantProperties.find((p) => String(p.id) === String(form.propertyId))?.name || form.propertyName || null)
          : null,
        workflowCode: form.workflowCode.trim(),
        workflowName: form.workflowName.trim(),
        workflowType: form.workflowType.trim().toUpperCase(),
        description: form.description,
        status: form.status,
        createdBy: getUserName(),
        updatedBy: getUserName(),
        steps: form.steps.map((s, idx) => {
          const role = tenantRoles.find((r) => String(r.roleId) === String(s.assignedRoleId));
          const toSeq = (v) => {
            if (v === '' || v === null || v === undefined) return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
          };
          return {
            approvalWorkflowStepId: s.approvalWorkflowStepId || null,
            stepSequence: idx + 1,
            stepCode: (s.stepCode || `STEP_${idx + 1}`).trim(),
            stepName: s.stepName.trim(),
            stepType: s.stepType || 'APPROVAL',
            assignedRoleId: s.assignedRoleId || null,
            assignedRoleName: role?.roleName || s.assignedRoleName || null,
            allowedActions: parseActions(s.allowedActions).join(','),
            conditionExpression: buildConditionJson({
              join: s.conditionJoin,
              clauses: s.conditionClauses,
            }) || s.conditionExpression?.trim() || null,
            nextStepOnTrue: toSeq(s.nextStepOnTrue),
            nextStepOnFalse: toSeq(s.nextStepOnFalse),
            nextStepOnApprove: toSeq(s.nextStepOnApprove),
            nextStepOnReject: toSeq(s.nextStepOnReject),
            formulaExpression: s.formulaExpression?.trim() || null,
            status: s.status || 'ACTIVE',
          };
        }),
      };
      if (editingId) {
        await apiFetch(`conductor/approval-workflows/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showSuccess('Workflow updated');
      } else {
        await apiFetch('conductor/approval-workflows', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showSuccess('Workflow created');
      }
      setDialogOpen(false);
      setSelectedTenantId(String(saveTenantId));
      await loadWorkflows(String(saveTenantId));
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(
    () => (workflows || []).map((wf) => ({
      id: wf.approvalWorkflowMasterId,
      tenantId: wf.tenantId,
      tenantName: tenants.find((t) => String(t.id) === String(wf.tenantId))?.name || '',
      propertyName: wf.propertyName || (wf.propertyId ? String(wf.propertyId) : 'Tenant default'),
      propertyId: wf.propertyId || '',
      workflowName: wf.workflowName,
      workflowCode: wf.workflowCode,
      workflowType: wf.workflowType,
      status: wf.status,
      stepCount: Array.isArray(wf.steps) ? wf.steps.length : 0,
      stepsSummary: Array.isArray(wf.steps)
        ? wf.steps.map((s) => s.stepName).filter(Boolean).join(' → ')
        : '',
      description: wf.description || '',
      raw: wf,
    })),
    [workflows, tenants],
  );

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel],
  );

  const openStepsDrawer = (wf) => {
    setStepsDrawerWorkflow(wf || null);
    setStepsDrawerOpen(Boolean(wf));
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete?.id) return;
    const deleteTenantId = rowPendingDelete.tenantId || selectedTenantId;
    if (!deleteTenantId) return;
    setDeleteLoading(true);
    try {
      await apiFetch(
        `conductor/approval-workflows/tenant/${deleteTenantId}/${rowPendingDelete.id}`,
        { method: 'DELETE' },
      );
      showSuccess('Workflow deleted');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      await loadWorkflows(selectedTenantId);
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'workflowName', headerName: 'Workflow Name', flex: 1.2, minWidth: 180 },
      { field: 'tenantName', headerName: 'Tenant', width: 180 },
      { field: 'propertyName', headerName: 'Business Unit', width: 180 },
      { field: 'workflowCode', headerName: 'Code', width: 160 },
      { field: 'workflowType', headerName: 'Type', width: 160, valueFormatter: ({ value }) => {
        const opt = WORKFLOW_TYPE_OPTIONS.find((o) => o.value === value);
        return opt?.label || value;
      } },
      { field: 'stepCount', headerName: 'Steps', width: 90 },
      { field: 'stepsSummary', headerName: 'Step Flow', flex: 1.4, minWidth: 220 },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value || '-'}
            size="small"
            sx={{
              bgcolor: params.value === 'ACTIVE' ? ht.green : ht.line,
              color: params.value === 'ACTIVE' ? ht.white : ht.text,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'View',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="View steps">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openStepsDrawer(params.row.raw);
              }}
              sx={harmonateGridActionIconSx}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const title = useMemo(
    () => (editingId ? 'Edit Workflow' : 'Create Workflow'),
    [editingId],
  );

  if (!harmonateOnly) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3 }}>
        <Alert severity="warning">
          Workflow setup is a Harmonate option. It is not available for tenant users.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Workflow
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Tooltip title={canDelete ? 'Delete' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => {
                  setRowPendingDelete(selectedRow);
                  setDeleteOpen(true);
                }}
                disabled={!selectedRow || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit' : 'No edit permission'}>
            <span>
              <IconButton
                onClick={() => selectedRow && openEdit(selectedRow.raw)}
                disabled={!selectedRow || !canEdit}
                sx={harmonatePlainIconButtonSx}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add' : 'No add permission'}>
            <span>
              <IconButton onClick={openCreate} disabled={!selectedTenantId || !canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {!selectedTenantId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Select a tenant to view and configure workflows for that tenant.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          onRowDoubleClick={(params) => openEdit(params.row.raw)}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={stepsDrawerOpen}
        onClose={() => setStepsDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={harmonateDrawerHeaderFlushSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Workflow Steps
            </Typography>
            <Typography variant="body2" sx={{ color: ht.white, fontSize: '0.875rem' }}>
              {stepsDrawerWorkflow?.workflowName || '-'}
            </Typography>
            <Typography variant="caption" sx={{ color: ht.white, display: 'block' }}>
              {tenants.find((t) => String(t.id) === String(stepsDrawerWorkflow?.tenantId))?.name
                || selectedTenantId} · {stepsDrawerWorkflow?.propertyName || (stepsDrawerWorkflow?.propertyId ? 'Business Unit' : 'Tenant default')} · {stepsDrawerWorkflow?.workflowCode} · {WORKFLOW_TYPE_OPTIONS.find((o) => o.value === stepsDrawerWorkflow?.workflowType)?.label || stepsDrawerWorkflow?.workflowType} · {stepsDrawerWorkflow?.status}
            </Typography>
          </Box>
          <IconButton onClick={() => setStepsDrawerOpen(false)} size="small" sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1.5} sx={{ overflow: 'auto', flex: 1 }}>
            {(stepsDrawerWorkflow?.steps || []).length === 0 ? (
              <Alert severity="info">No steps configured.</Alert>
            ) : (
              (stepsDrawerWorkflow.steps || []).map((step) => (
                <Paper key={step.approvalWorkflowStepId || step.stepSequence} variant="outlined" sx={{ ...harmonateCardSx, p: 1.5, bgcolor: ht.white }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text }}>
                    {step.stepSequence}. {step.stepName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: ht.fadedText, display: 'block' }}>
                    Code: {step.stepCode || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: ht.fadedText, display: 'block' }}>
                    Role: {step.assignedRoleName || 'Any'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: ht.fadedText, display: 'block' }}>
                    Actions: {step.allowedActions || 'APPROVE,REJECT'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: ht.fadedText, display: 'block' }}>
                    Condition: {step.conditionExpression || 'Always run'}
                  </Typography>
                </Paper>
              ))
            )}
          </Stack>
          <Box sx={{ pt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EditIcon />}
              disabled={!stepsDrawerWorkflow || !canEdit}
              onClick={() => {
                setStepsDrawerOpen(false);
                openEdit(stepsDrawerWorkflow);
              }}
              sx={harmonateCancelButtonSx}
            >
              Edit Workflow
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md" PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{title}</DialogTitle>
        <DialogContent dividers sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="Tenant"
                value={form.tenantId || ''}
                onChange={(e) => handleFormTenantChange(e.target.value)}
                disabled={Boolean(editingId)}
                helperText={editingId
                  ? 'Tenant cannot be changed after create'
                  : 'Workflow is saved for this tenant. Optional business unit below scopes it; otherwise it is the one tenant default.'}
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.875rem' }}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Business Unit (optional)"
                value={form.propertyId || ''}
                onChange={(e) => {
                  const propertyId = e.target.value;
                  const property = tenantProperties.find((p) => String(p.id) === String(propertyId));
                  setForm((p) => ({
                    ...p,
                    propertyId,
                    propertyName: property?.name || '',
                  }));
                }}
                helperText="Leave empty for the tenant default (only one active default per type). If set, only that business unit uses this workflow."
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                  All business units (tenant default)
                </MenuItem>
                {tenantProperties.map((p) => (
                  <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.875rem' }}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Workflow Name"
                value={form.workflowName}
                onChange={(e) => setForm((p) => ({ ...p, workflowName: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                required
                label="Workflow Code"
                value={form.workflowCode}
                onChange={(e) => setForm((p) => ({ ...p, workflowCode: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="Workflow Type"
                value={form.workflowType || 'AP_INVOICE'}
                onChange={(e) => setForm((p) => ({ ...p, workflowType: e.target.value }))}
                helperText={
                  WORKFLOW_TYPE_OPTIONS.find((o) => o.value === form.workflowType)?.helper
                  || 'Select the document this workflow applies to'
                }
                SelectProps={{
                  displayEmpty: false,
                  renderValue: (selected) => {
                    const opt = WORKFLOW_TYPE_OPTIONS.find((o) => o.value === selected);
                    return opt?.label || selected;
                  },
                  MenuProps: harmonateSelectMenuProps,
                  onOpen: (event) => {
                    const selectRoot = event.currentTarget;
                    const width = selectRoot?.offsetWidth;
                    // Next tick: paper is mounted
                    requestAnimationFrame(() => {
                      const papers = document.querySelectorAll('.MuiMenu-paper');
                      const paper = papers[papers.length - 1];
                      if (paper && width) {
                        paper.style.width = `${width}px`;
                        paper.style.maxWidth = `${width}px`;
                      }
                    });
                  },
                }}
                sx={{
                  '& .MuiInputBase-input': { fontSize: '0.875rem' },
                  '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              >
                {WORKFLOW_TYPE_OPTIONS.filter((o) => o.enabled).map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
                {form.workflowType
                  && !WORKFLOW_TYPE_OPTIONS.some((o) => o.value === form.workflowType && o.enabled) && (
                  <MenuItem value={form.workflowType} sx={{ fontSize: '0.875rem' }}>
                    {form.workflowType}
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
            <Typography variant="subtitle2" sx={harmonateSectionHeaderSx}>
              Steps (condition / branch / formula)
            </Typography>
            <Button size="small" onClick={addStep} sx={{ textTransform: 'none' }}>
              Add Step
            </Button>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Submit starts the workflow and waits on Step 1. Business-unit workflow is used first; if none, the tenant default is used.
            Only one active tenant default (and one active workflow per business unit) is allowed per type.
            VIEW = Next. APPROVAL = Approve/Reject. Combine conditions with AND/OR on one approval step.
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
              Available variables ({workflowTypeLabel})
              {catalogFromDb ? ' — from catalog' : ''}
            </Typography>
            {catalogLoading && (
              <Typography variant="caption" color="text.secondary">Loading variables…</Typography>
            )}
            {!catalogLoading && catalogError && (
              <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
                {catalogError}
              </Typography>
            )}
            {!catalogLoading && !availableVariables.length && !catalogError && (
              <Typography variant="caption" color="text.secondary">No variables configured.</Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {availableVariables.map((v) => (
                <Chip
                  key={v.key}
                  size="small"
                  label={`${v.label} (${v.key})`}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          </Paper>
          <Stack spacing={2}>
            {form.steps.map((step, index) => {
              const parsed = parseConditionBuilder(step.conditionExpression);
              const conditionBuilder = {
                join: step.conditionJoin || parsed.join || 'AND',
                clauses: (step.conditionClauses && step.conditionClauses.length)
                  ? step.conditionClauses
                  : parsed.clauses,
              };
              return (
              <Paper key={`step-${index}`} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Step {index + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => removeStep(index)} disabled={form.steps.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Step Name"
                      value={step.stepName}
                      onChange={(e) => updateStep(index, { stepName: e.target.value })}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Step Type"
                      value={step.stepType || 'APPROVAL'}
                      onChange={(e) => {
                        const stepType = e.target.value;
                        if (stepType === 'VIEW') {
                          updateStep(index, {
                            stepType,
                            allowedActions: [...VIEW_ACTIONS],
                            conditionExpression: '',
                            conditionJoin: 'AND',
                            conditionClauses: [emptyConditionClause()],
                          });
                        } else if (stepType === 'APPROVAL') {
                          updateStep(index, {
                            stepType,
                            allowedActions: [...APPROVAL_ACTIONS],
                          });
                        } else {
                          updateStep(index, { stepType });
                        }
                      }}
                      helperText="VIEW = review then Next. APPROVAL = Approve/Reject (conditions apply here)."
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    >
                      <MenuItem value="VIEW">VIEW</MenuItem>
                      <MenuItem value="APPROVAL">APPROVAL</MenuItem>
                      <MenuItem value="FORMULA">FORMULA</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Assigned Role"
                      value={step.assignedRoleId || ''}
                      onChange={(e) => {
                        const roleId = e.target.value;
                        const role = tenantRoles.find((r) => String(r.roleId) === String(roleId));
                        updateStep(index, {
                          assignedRoleId: roleId,
                          assignedRoleName: role?.roleName || '',
                        });
                      }}
                      helperText={
                        tenantRoles.length
                          ? `Roles for ${formTenant?.name || 'selected tenant'}`
                          : 'No roles found for this tenant'
                      }
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {tenantRoles.map((role) => (
                        <MenuItem key={role.roleId} value={role.roleId}>
                          {role.roleName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`allowed-actions-${index}`}>Allowed Actions</InputLabel>
                      <Select
                        labelId={`allowed-actions-${index}`}
                        multiple
                        value={parseActions(step.allowedActions)}
                        onChange={(e) => {
                          const next = typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value;
                          // Keep at least one action selected
                          if (!next.length) return;
                          updateStep(index, { allowedActions: next });
                        }}
                        input={<OutlinedInput label="Allowed Actions" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                onDelete={
                                  selected.length > 1
                                    ? (event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        updateStep(index, {
                                          allowedActions: selected.filter((a) => a !== value),
                                        });
                                      }
                                    : undefined
                                }
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        sx={{ '& .MuiSelect-select': { fontSize: '0.875rem' } }}
                      >
                        {((step.stepType || 'APPROVAL') === 'VIEW' ? VIEW_ACTIONS : APPROVAL_ACTIONS).map((action) => (
                          <MenuItem key={action} value={action}>
                            {action}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {(step.stepType || 'APPROVAL') === 'VIEW'
                        ? 'VIEW steps use Next only.'
                        : 'APPROVE and REJECT are the defaults. At least one is required.'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Step Code"
                      value={step.stepCode}
                      onChange={(e) => updateStep(index, { stepCode: e.target.value })}
                      placeholder="Optional, auto-filled if blank"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                  {(step.stepType || 'APPROVAL') === 'VIEW' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        VIEW steps have no condition. The assigned role reviews the invoice and clicks Next.
                      </Typography>
                    </Grid>
                  )}
                  {(step.stepType || 'APPROVAL') === 'APPROVAL' && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Condition (checked on Approve)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => applyConditionBuilder(index, { add: true })}
                        sx={{ textTransform: 'none' }}
                      >
                        Add condition
                      </Button>
                    </Box>
                    {conditionBuilder.clauses.length > 1 && (
                      <TextField
                        select
                        size="small"
                        label="Match"
                        value={conditionBuilder.join || 'AND'}
                        onChange={(e) => applyConditionBuilder(index, { join: e.target.value })}
                        sx={{ mb: 1, minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                      >
                        <MenuItem value="AND">AND (all must be true)</MenuItem>
                        <MenuItem value="OR">OR (any can be true)</MenuItem>
                      </TextField>
                    )}
                    <Stack spacing={1}>
                      {conditionBuilder.clauses.map((clause, clauseIndex) => {
                        const needsValue = clause.op !== 'IS_NULL' && clause.op !== 'IS_NOT_NULL';
                        return (
                          <Grid container spacing={1} key={`cond-${index}-${clauseIndex}`} alignItems="center">
                            <Grid item xs={12} md={5}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={clauseIndex === 0 ? 'Variable' : `Variable ${clauseIndex + 1}`}
                                value={clause.field || ''}
                                onChange={(e) => applyConditionBuilder(index, {
                                  clauseIndex,
                                  patch: { field: e.target.value },
                                })}
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                              >
                                <MenuItem value="">
                                  <em>{clauseIndex === 0 ? 'None (always run)' : 'None'}</em>
                                </MenuItem>
                                {availableVariables.map((v) => (
                                  <MenuItem key={v.key} value={v.key}>
                                    {v.group}: {v.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Operator"
                                value={clause.op || 'GTE'}
                                onChange={(e) => applyConditionBuilder(index, {
                                  clauseIndex,
                                  patch: { op: e.target.value },
                                })}
                                disabled={!clause.field}
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                              >
                                {CONDITION_OPS.map((op) => (
                                  <MenuItem key={op.value} value={op.value}>
                                    {op.label} ({op.value})
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={conditionBuilder.clauses.length > 1 ? 5 : 6} md={conditionBuilder.clauses.length > 1 ? 3 : 4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Value"
                                value={clause.value || ''}
                                onChange={(e) => applyConditionBuilder(index, {
                                  clauseIndex,
                                  patch: { value: e.target.value },
                                })}
                                disabled={!clause.field || !needsValue}
                                placeholder="e.g. 100000"
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                              />
                            </Grid>
                            {conditionBuilder.clauses.length > 1 && (
                              <Grid item xs={1} md={1}>
                                <IconButton
                                  size="small"
                                  onClick={() => applyConditionBuilder(index, { remove: clauseIndex })}
                                  aria-label="Remove condition"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        );
                      })}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                      {step.conditionExpression
                        ? `Rule: ${step.conditionExpression}`
                        : 'No condition — Approve always moves to the next step.'}
                      {' '}
                      Use AND/OR to combine rules (amount and new vendor, amount or over budget).
                      Checked only when Approve is clicked. If false and On False is empty, the step holds.
                    </Typography>
                  </Grid>
                  )}
                  {(step.stepType || 'APPROVAL') === 'FORMULA' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Formula Expression (JSON)"
                        value={step.formulaExpression || ''}
                        onChange={(e) => updateStep(index, { formulaExpression: e.target.value })}
                        placeholder='{"set":"computedTotal","op":"MUL","field":"invoice_amount","value":"1.1"}'
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                  )}
                  {(step.stepType || 'APPROVAL') === 'APPROVAL' && (
                  <>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="On True → Step #"
                      value={step.nextStepOnTrue ?? ''}
                      onChange={(e) => updateStep(index, { nextStepOnTrue: e.target.value })}
                      placeholder="next"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="On False → Step #"
                      value={step.nextStepOnFalse ?? ''}
                      onChange={(e) => updateStep(index, { nextStepOnFalse: e.target.value })}
                      placeholder="hold if empty"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                  </>
                  )}
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={(step.stepType || 'APPROVAL') === 'VIEW' ? 'On Next → Step #' : 'On Approve → Step #'}
                      value={step.nextStepOnApprove ?? ''}
                      onChange={(e) => updateStep(index, { nextStepOnApprove: e.target.value })}
                      placeholder="next"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="On Reject → Step #"
                      value={step.nextStepOnReject ?? ''}
                      onChange={(e) => updateStep(index, { nextStepOnReject: e.target.value })}
                      placeholder="end reject"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={harmonateProceedButtonSx}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete Workflow</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete &quot;{rowPendingDelete?.workflowName || 'selected'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default ApWorkflowSetup;
